/* -------------------------------------------------------------------------- */
/* Currency — one definition, shared by the API and both frontends             */
/*                                                                            */
/* Before this, the tenant app carried TWO duplicate currency enums            */
/* (`FinanceCurrency` and `CurrencyCode`) and the API carried none: it read a  */
/* bare `workspace.baseCurrency` string with a silent `?? "USD"` fallback. A   */
/* value set with no single definition is a value set nobody validates.        */
/* -------------------------------------------------------------------------- */

/** The currencies Ibada Cloud supports. Extending this is a deliberate act — see MINOR_UNITS below. */
export enum Currency {
  USD = "USD",
  EUR = "EUR",
  /** Congolese franc — the home market. */
  CDF = "CDF",
  RWF = "RWF",
}

/**
 * ISO 4217 minor units: how many decimal places the currency actually HAS.
 *
 * This is not cosmetic. `money()` hard-coded 2 decimal places, which silently invents a fractional unit that
 * does not exist: **RWF has no minor unit at all** (ISO 4217 exponent 0), so "1,500.75 RWF" is not a real
 * amount. CDF and EUR and USD do have 2 (the Congolese franc has centimes, whatever the street practice).
 *
 * Rounding must derive from the currency, never from a constant.
 */
export const CURRENCY_MINOR_UNITS: Readonly<Record<Currency, number>> = Object.freeze({
  [Currency.USD]: 2,
  [Currency.EUR]: 2,
  [Currency.CDF]: 2,
  [Currency.RWF]: 0,
});

/** Decimal places for a currency. Unknown codes fall back to 2 — the safe majority case, never a silent zero. */
export const currencyScale = (currency: string): number =>
  CURRENCY_MINOR_UNITS[currency as Currency] ?? 2;

/** Whether a raw string is a currency this system actually supports. */
export const isSupportedCurrency = (value: string): value is Currency =>
  Object.values(Currency).includes(value as Currency);

/* -------------------------------------------------------------------------- */
/* Currency APPEARANCE — the ONE place that decides how a currency is shown.    */
/*                                                                            */
/* There must never be a second. When the symbol lived in several hand-written */
/* switches, the same currency showed as "FR", "RF", "RWF" and "FRw" on        */
/* different screens. Every formatter and every input prefix in the app derives */
/* from the functions below, so a label can never disagree with an amount.      */
/* -------------------------------------------------------------------------- */

/**
 * Currencies whose narrow symbol is a universally recognized glyph (`$`, `€`). For every OTHER currency Intl's
 * `"narrowSymbol"` resolves to an ambiguous letter form — the Rwandan franc becomes `FR`, colliding with half a
 * dozen African francs — so we show the unambiguous ISO code (`RWF`, `CDF`) instead. Keeping the glyph for the
 * currencies that actually have one is the whole reason we don't just switch everything to `"code"`.
 */
const GLYPH_SYMBOL_CURRENCIES: ReadonlySet<string> = new Set(["USD", "EUR", "GBP", "JPY", "CNY", "INR", "KRW"]);

/** Whether a currency renders as its glyph (`$`, `€`) or its ISO code (`RWF`, `CDF`). */
export const currencyDisplayFor = (currency: string): "narrowSymbol" | "code" =>
  GLYPH_SYMBOL_CURRENCIES.has(currency.trim().toUpperCase()) ? "narrowSymbol" : "code";

/**
 * Canonical `Intl` options for rendering an amount in a currency — the single source of truth for money display.
 *
 * There is deliberately NO `maximumFractionDigits`: `Intl` already knows every ISO-4217 currency's minor units
 * (RWF has none), and pinning a constant is what once printed Rwandan francs to a hundredth of a franc that does
 * not exist.
 */
export const currencyFormatOptions = (currency: string): Intl.NumberFormatOptions => ({
  style: "currency",
  currencyDisplay: currencyDisplayFor(currency),
});

/**
 * The standalone symbol/code for a currency (`$`, `€`, `RWF`, `CDF`) — the prefix shown before a bare, editable
 * number (money inputs). Derived from the SAME decision as full formatting, so a prefix can never disagree with a
 * formatted amount. Falls back to the uppercased code for anything `Intl` cannot format.
 */
export const currencySymbol = (currency: string, locale = "en-US"): string => {
  const code = (currency ?? "").trim();
  if (!code) return "";
  try {
    const parts = new Intl.NumberFormat(locale, { ...currencyFormatOptions(code), currency: code }).formatToParts(0);
    return parts.find((part) => part.type === "currency")?.value ?? code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
};
